import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaApple,
  FaArrowRight,
  FaEnvelope,
  FaFacebookF,
  FaGooglePlay,
  FaHeart,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTruck,
  FaWhatsapp,
} from 'react-icons/fa';

const COMPANY_NAME = 'KKISANFRESH DAILY PRIVATE LIMITED';
const TRADEMARK_NAME = 'KISAN DAILY FRESH'

const Footer = () => {
  const navigate = useNavigate();
  const footerLogoSrc = '/k_kisanlogo.png';

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'Products', path: '/products' },
    { label: 'About Us', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Privacy Policy', path: '/privacy-policy' },
  ];

  const categories = ['Vegetables', 'Fruits', 'Dairy Products', 'Grains', 'Organic Herbs', 'Fresh Juices'];

  const socials = [
    { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61590549803646', Icon: FaFacebookF },
    { label: 'Instagram', href: '#', Icon: FaInstagram },
    { label: 'WhatsApp', href: '#', Icon: FaWhatsapp },
  ];

  const contacts = [
    { label: '+91 9699664866', Icon: FaPhoneAlt },
    { label: 'info@kisandailyfresh.in', href: 'mailto:info@kisandailyfresh.in', Icon: FaEnvelope },
    { label: 'Pune Maharashtra', Icon: FaMapMarkerAlt },
  ];

  return (
    <footer className="bg-gray-950 text-white">
      <div className="bg-gradient-to-r from-primary-700 to-primary-600">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="flex items-center gap-2 text-sm font-medium text-white">
              <FaTruck className="h-4 w-4" aria-hidden="true" />
              <span>
                Free delivery on orders above <span className="font-bold">Rs 500</span> - Order now!
              </span>
            </p>
            <button
              onClick={() => navigate('/products')}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-bold text-primary-700 shadow transition hover:bg-primary-50"
            >
              Shop Now
              <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
        <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-4">
          <div className="col-span-2 lg:col-span-1">
            <div className="cursor-pointer" onClick={() => navigate('/')}>
              <img src={footerLogoSrc} alt="KKisan" className="h-12 w-auto" />
            </div>
            <p className="mt-3 text-sm font-bold uppercase tracking-wide text-primary-400">
              {COMPANY_NAME}
            </p>
            <p className="mt-3 text-xs leading-6 text-gray-400 sm:text-sm">
              Connecting farmers directly with consumers for the freshest organic produce, delivered to your doorstep.
            </p>
            <div className="mt-4 flex items-center gap-2">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-800 text-gray-400 transition hover:bg-primary-600 hover:text-white"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-primary-400 sm:mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map(({ label, path }) => (
                <li key={label}>
                  <button
                    onClick={() => navigate(path)}
                    className="group flex items-center gap-2 text-xs text-gray-400 transition hover:text-white sm:text-sm"
                  >
                    <span className="h-px w-3 bg-gray-700 transition-all group-hover:w-5 group-hover:bg-primary-500" />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-primary-400 sm:mb-4">Categories</h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat}>
                  <button
                    onClick={() => navigate('/products')}
                    className="group flex items-center gap-2 text-xs text-gray-400 transition hover:text-white sm:text-sm"
                  >
                    <span className="h-px w-3 bg-gray-700 transition-all group-hover:w-5 group-hover:bg-primary-500" />
                    {cat}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-widest text-primary-400">Contact Us</h4>
            <p className="mb-4 text-xs font-semibold uppercase leading-5 tracking-wide text-gray-300">
              {COMPANY_NAME}
            </p>
            <ul className="space-y-3">
              {contacts.map(({ label, href, Icon }) => (
                <li key={label} className="flex items-start gap-2">
                  <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-400" aria-hidden="true" />
                  {href ? (
                    <a href={href} className="text-xs leading-5 text-gray-400 transition hover:text-white sm:text-sm">
                      {label}
                    </a>
                  ) : (
                    <span className="text-xs leading-5 text-gray-400 sm:text-sm">{label}</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs text-gray-300">
                <FaGooglePlay className="h-3.5 w-3.5" aria-hidden="true" />
                Google Play
              </div>
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs text-gray-300">
                <FaApple className="h-3.5 w-3.5" aria-hidden="true" />
                App Store
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} {TRADEMARK_NAME}. All rights reserved.</p>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Made with</span>
              <FaHeart className="h-3 w-3 text-red-500" aria-hidden="true" />
              <span>for Indian farmers</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <button onClick={() => navigate('/privacy-policy')} className="transition hover:text-gray-300">Privacy</button>
              <span className="h-1 w-1 rounded-full bg-gray-700" aria-hidden="true" />
              <button className="transition hover:text-gray-300">Terms</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
